/**
 * Language utilities for video editing app
 */

// Supported languages for translation - Enhanced with more languages
export const SUPPORTED_LANGUAGES = [
  // Arabic variants 
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'ar-ma', name: 'العربية المغربية', flag: '🇲🇦' },
  { code: 'ar-eg', name: 'العربية المصرية', flag: '🇪🇬' },
  
  // Major international languages
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  
  // Middle East & Asia
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
  
  // European languages
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'cs', name: 'Čeština', flag: '🇨🇿' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' },
  { code: 'hr', name: 'Hrvatski', flag: '🇭🇷' },
  { code: 'sr', name: 'Српски', flag: '🇷🇸' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'lt', name: 'Lietuvių', flag: '🇱🇹' },
  { code: 'lv', name: 'Latviešu', flag: '🇱🇻' },
  { code: 'et', name: 'Eesti', flag: '🇪🇪' },
  { code: 'sl', name: 'Slovenščina', flag: '🇸🇮' }
];

/**
 * Get language info by code with enhanced support
 */
export const getLanguageInfo = (langCode) => {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === langCode) || { 
    code: langCode, 
    name: langCode.toUpperCase(), 
    flag: '🌐' 
  };
};

/**
 * Get languages used in the project
 */
export const getUsedLanguages = (project, individualPartsLang = null) => {
  const usedLangCodes = new Set();
  
  console.log('🔍 Checking project for languages:', {
    transcriptionLanguage: project?.transcriptionLanguage,
    hasTranscript: !!project?.transcript?.length,
    hasTranslations: !!Object.keys(project?.translations || {}).length,
    parts: project.parts?.length || 0,
    groups: project.groups?.length || 0,
    individualPartsLang: individualPartsLang
  });
  
  // 1. ALWAYS include original transcription language if transcript exists
  if (project?.transcript?.length > 0) {
    const originalLang = project.transcriptionLanguage || 'en';
    usedLangCodes.add(originalLang);
    console.log(`🎤 Original transcription language: ${originalLang}`);
  }
  
  // 2. Add languages from actual translations
  const translations = project?.translations || {};
  Object.keys(translations).forEach(langCode => {
    if (translations[langCode] && translations[langCode].length > 0) {
      usedLangCodes.add(langCode);
      console.log(`🌍 Translation language found: ${langCode}`);
    }
  });
  
  // 3. Add legacy translation if exists
  if (project?.translation?.length > 0 && project?.translationLanguage) {
    usedLangCodes.add(project.translationLanguage);
    console.log(`🌍 Legacy translation language: ${project.translationLanguage}`);
  }
  
  // 4. Check Individual Parts language (only if it has actual content)
  if (individualPartsLang && (usedLangCodes.has(individualPartsLang) || project?.transcript?.length > 0)) {
    usedLangCodes.add(individualPartsLang);
    console.log(`📄 Individual Parts language: ${individualPartsLang}`);
  }
  
  // 5. Check parts languages (only if they match available content languages)
  if (project.parts) {
    project.parts.forEach((part, index) => {
      if (part.language && usedLangCodes.has(part.language)) {
        console.log(`📄 Part ${index}: confirmed language ${part.language}`);
      }
    });
  }
  
  // 6. Check groups languages (only if they match available content languages)
  if (project.groups) {
    project.groups.forEach((group, groupIndex) => {
      if (group.language && usedLangCodes.has(group.language)) {
        console.log(`📁 Group ${groupIndex} (${group.name}): confirmed language ${group.language}`);
      }
    });
  }
  
  // Convert to language info objects
  const usedLanguages = Array.from(usedLangCodes).map(code => getLanguageInfo(code));
  
  // Sort: original language first, then alphabetically
  const originalLang = project?.transcriptionLanguage || 'en';
  usedLanguages.sort((a, b) => {
    if (a.code === originalLang && b.code !== originalLang) return -1;
    if (a.code !== originalLang && b.code === originalLang) return 1;
    return a.name.localeCompare(b.name);
  });
  
  console.log('📊 getUsedLanguages result:', {
    originalLanguage: originalLang,
    foundCodes: Array.from(usedLangCodes),
    usedLanguages: usedLanguages.map(l => `${l.flag} ${l.name}`),
    hasMultiple: usedLanguages.length > 1,
    totalLanguages: usedLanguages.length
  });
  
  return usedLanguages;
}; 